import { RewardId, RewardResult } from './RewardTypes';
import { REWARD_CONFIGS } from './RewardConfig';
import { computeDungeonGoldReward, GOLD_CHEST_MULTIPLIER } from './GoldRewardConfig';

/**
 * Reward system handles spawning rewards for chests
 */
export class RewardSystem {
  private dungeonLevel: number;
  private playerGoldAtEntry: number;
  private chestIndex: number = 0;

  constructor(dungeonLevel: number = 1, playerGoldAtEntry: number = 0) {
    this.dungeonLevel = dungeonLevel;
    this.playerGoldAtEntry = playerGoldAtEntry;
  }

  /**
   * Get the reward for the next chest.
   * First chest always gets GOLDEN_KEY.
   * Levels 1–2: remaining chests are always gold (early dungeons are forgiving).
   * Level 3+: random rewards based on chanceToSpawn.
   */
  public getNextChestReward(): RewardResult {
    const isFirstChest = this.chestIndex === 0;
    this.chestIndex++;

    if (isFirstChest) {
      // First chest always has golden key
      return this.rollReward(RewardId.GOLDEN_KEY);
    }

    // Early dungeon levels: only gold besides the guaranteed golden key
    if (this.dungeonLevel <= 2) {
      return this.rollReward(RewardId.GOLD);
    }

    return this.rollRandomReward();
  }

  /**
   * Roll for a random reward based on chanceToSpawn probabilities
   */
  private rollRandomReward(): RewardResult {
    // Calculate total weight for normalization
    const totalWeight = REWARD_CONFIGS.reduce((sum, config) => sum + config.chanceToSpawn, 0);

    // Roll a random number
    let roll = Math.random() * totalWeight;

    // Find which reward we landed on
    for (const config of REWARD_CONFIGS) {
      roll -= config.chanceToSpawn;
      if (roll <= 0) {
        return this.rollReward(config.id);
      }
    }

    // Fallback (should never happen)
    return this.rollReward(RewardId.NOTHING);
  }

  /**
   * Roll for a specific reward, calculating the actual amount
   */
  private rollReward(rewardId: RewardId): RewardResult {
    const config = REWARD_CONFIGS.find(c => c.id === rewardId);

    if (!config) {
      console.error(`Reward config not found for ${rewardId}`);
      return { id: RewardId.NOTHING, amount: 0 };
    }

    // Gold: computed from player gold at entry × level × chest multiplier
    if (rewardId === RewardId.GOLD) {
      const amount = computeDungeonGoldReward(
        this.playerGoldAtEntry,
        this.dungeonLevel,
        GOLD_CHEST_MULTIPLIER
      );
      return { id: rewardId, amount };
    }

    // Other rewards: use config amount (and optional maxAmount)
    let amount = config.amount;
    if (config.maxAmount !== undefined) {
      amount = Math.floor(Math.random() * (config.maxAmount - amount + 1)) + amount;
    }

    return {
      id: rewardId,
      amount,
    };
  }

  /**
   * Reset the chest index (for new dungeon/level)
   */
  public reset(): void {
    this.chestIndex = 0;
  }

  /**
   * Get current dungeon level
   */
  public getDungeonLevel(): number {
    return this.dungeonLevel;
  }

  /**
   * Set dungeon level (affects gold amounts)
   */
  public setDungeonLevel(level: number): void {
    this.dungeonLevel = level;
  }
}